/**
 * Filters the main product media gallery (carousel slides + thumbnails) so only the
 * media belonging to the selected "Carnation" option value is shown. Built as an
 * additive, self-guarding script: it no-ops on any product that has no Carnation
 * option, and never touches Horizon's own variant/media data model.
 *
 * Media-to-group association is read from each image's alt text, formatted as:
 *   "{Carnation value} — {description}"
 * e.g. "Light — Collant thermique sculptant 01, vue 1"
 *
 * Bug fixes (both caused by assumptions that broke once hidden slides entered
 * the picture — Horizon's own slideshow has no concept of "filtered" media):
 *
 * 1. Clicking slide 2/3/... of one colour would scroll through the PREVIOUS
 *    colour's slides. Horizon's native thumbnails render with a static
 *    `on:click="/select/N"` where N is the slide's position among ALL media
 *    (not just the visible colour's). We used to rewrite N to the colour-
 *    relative index, but only for the currently active colour's thumbnails —
 *    and only once, whenever that colour last became active. If the gallery's
 *    hidden/visible state had moved on since then (e.g. after Shopify
 *    re-renders the gallery on a variant change), that baked-in index could
 *    point at the wrong slide. Fixed by not pre-baking an index at all: each
 *    thumbnail now gets one real click listener that looks up its slide's
 *    CURRENT index (via the native slideshow's own `slides` getter, which
 *    already filters out `[hidden]` slides) at the moment it's clicked, then
 *    calls `slideshow.select()` directly — so it can never go stale.
 *
 * 2. Swiping a slide on mobile did nothing (snapped back instantly). The
 *    MutationObserver watched the ENTIRE product-details subtree (price,
 *    quantity, variant form, etc.), and `applyFilter()` unconditionally
 *    force-selected slide 0 on every single run — including runs triggered
 *    by Horizon's own internal DOM updates while a drag/swipe was settling.
 *    Fixed two ways: the observer now only watches the gallery element
 *    itself (still enough to catch Shopify re-rendering its media on a
 *    variant change), and slide 0 is only force-selected when the selected
 *    Carnation actually changed since the last run — not on every mutation.
 *
 * 3. Dragging/swiping past the current colour's last slide scrolled straight
 *    into the NEXT colour's (supposedly hidden) slides. The theme's own CSS
 *    rule `slideshow-slide[hidden]:not([reveal]) { display: none }` is
 *    losing a cascade-layer fight against a later `.product-media-container
 *    { display: flex }` rule, so a [hidden] slide stayed laid out at full
 *    width — invisible to Horizon's own `slides` getter (so clicks/JS
 *    navigation were fine, per fix #1) but still physically reachable by a
 *    raw horizontal scroll/drag gesture, which scrolls the real DOM, not a
 *    filtered list. Fixed by also forcing `display: none` as an inline
 *    style (inline styles win over stylesheet rules regardless of cascade
 *    layer/specificity) on every non-matching slide, cleared again once it
 *    matches.
 *
 * 4. On desktop, clicking a thumbnail (especially jumping more than one
 *    slide away, e.g. first -> last) intermittently left the WRONG
 *    thumbnail highlighted — sometimes it looked like clicking, or going
 *    back to an earlier slide, "did nothing". Root cause: Horizon's own
 *    `Slideshow#current` setter highlights thumbnails by writing
 *    `aria-selected` using the slide's COLOUR-RELATIVE index (from our
 *    filtered `slides` getter) but against `this.refs.thumbnails`, which is
 *    the GLOBAL, unfiltered list of every thumbnail in the gallery — so e.g.
 *    index 4 (this colour's 5th slide) highlights whatever thumbnail
 *    happens to sit at position 4 in the full, all-colours list. We already
 *    corrected this reactively on the `slideshow:select` event, but for
 *    non-adjacent jumps Horizon's internal scroll-settle logic can call the
 *    `current` setter a SECOND time afterwards (via its own scroll sync)
 *    without emitting another event, silently re-breaking our fix after the
 *    fact — a race, which is why it only happened sometimes. Fixed by also
 *    watching `aria-selected` itself via MutationObserver, so any native
 *    rewrite — whenever it happens — gets self-corrected immediately
 *    instead of relying solely on the one event.
 *
 * 5. On desktop, reaching the LAST slide of a colour and clicking
 *    next (or the FIRST slide and clicking previous) broke navigation
 *    completely from then on — both the arrows and the thumbnails stopped
 *    working. Root cause: the gallery has Horizon's `infinite` wraparound
 *    enabled, so `next()`/`previous()` request an index one past either end
 *    (e.g. index 4 of a 4-slide colour) and rely on `select()` to wrap it
 *    back to 0 — but that relies on `this.refs.slides` (the GLOBAL,
 *    unfiltered, all-colours list) and `this.slides` (our colour-filtered
 *    one) staying in sync, and `select()` also physically moves the target
 *    slide element in the DOM via a "reorder trick" for this kind of jump.
 *    Once that trick runs against an out-of-range index, Horizon's index
 *    bookkeeping and our colour filter disagree about what "slide 0" even
 *    is, landing on the wrong slide (e.g. a different colour's) or on
 *    nothing — which then keeps repeating since every subsequent click
 *    starts from that same corrupted state. Fixed the same way as fix #1:
 *    the arrow buttons get their own click handler that computes the
 *    colour-relative next/previous slide itself (wrapping within just this
 *    colour's own slide count) and calls `slideshow.select()` with that
 *    already in-range index directly — so Horizon's own
 *    wrap/clamp/reorder-trick path, the thing that was breaking, never runs.
 */
(function () {
  'use strict';

  var OPTION_NAME = 'Carnation';

  function parseGroup(alt) {
    if (!alt) return '';
    var sep = alt.indexOf('—'); // em dash —
    var head = sep > -1 ? alt.slice(0, sep) : alt;
    return head.trim().toLowerCase();
  }

  function isInDialog(el) {
    return !!el.closest('dialog');
  }

  function getSelectedGroup(root) {
    var input = root.querySelector('input[data-option-name="' + OPTION_NAME + '"]:checked');
    return input ? input.value.trim().toLowerCase() : null;
  }

  function bindThumbnailClick(btn, gallery) {
    if (btn.__mpClickBound) return;
    btn.__mpClickBound = true;

    // Remove Horizon's own static declarative handler so it can't fire with
    // its original (now potentially wrong, once slides are hidden) index.
    btn.removeAttribute('on:click');

    btn.addEventListener('click', function (event) {
      var slideshow = gallery.slideshow;
      if (!slideshow) return;

      var img = btn.querySelector('img[alt]');
      var altText = img ? img.alt : '';
      if (!altText) return;

      // this.slides on the native component already filters out [hidden]
      // slides, so this index is always correct for whatever is visible
      // right now — computed fresh on every click, never stored/stale.
      var slides = slideshow.slides || [];
      var targetSlide = slides.find(function (s) {
        var simg = s.querySelector('img[alt]');
        return simg && simg.alt === altText;
      });
      if (!targetSlide) return;

      event.preventDefault();
      slideshow.select(slides.indexOf(targetSlide), event);
    });
  }

  function bindArrowClick(btn, gallery, direction) {
    if (!btn || btn.__mpClickBound) return;
    btn.__mpClickBound = true;

    // Remove Horizon's own static declarative handler ("/next" or
    // "/previous") — it's what drives the buggy out-of-range wrap.
    btn.removeAttribute('on:click');

    btn.addEventListener('click', function (event) {
      var slideshow = gallery.slideshow;
      if (!slideshow) return;

      // this.slides is already colour-filtered (excludes [hidden] slides),
      // so wrapping within its own length keeps the target index in range —
      // Horizon's own clamp/wrap/reorder-trick path never has to run.
      var slides = slideshow.slides || [];
      if (!slides.length) return;

      var current = slideshow.current;
      var targetIndex =
        direction === 'next' ? (current + 1) % slides.length : (current - 1 + slides.length) % slides.length;

      event.preventDefault();
      slideshow.select(targetIndex, event);
    });
  }

  function applyFilter(root, state) {
    var group = getSelectedGroup(root);
    if (!group) return;

    var gallery = root.querySelector('media-gallery');
    if (!gallery) return;

    // During a teinte transition the gallery is hidden via CSS. Skip
    // filtering the OLD gallery (which is missing the new teinte's hero
    // image) — wait until section rendering replaces it with a new one.
    if (root.classList.contains('carnation-transitioning') && gallery === state.lastGallery) {
      return;
    }

    var slides = Array.prototype.filter.call(gallery.querySelectorAll('slideshow-slide'), function (el) {
      return !isInDialog(el);
    });

    var galleryReplaced = gallery !== state.lastGallery;
    var groupChanged = state.lastGroup !== group;
    state.lastGroup = group;
    state.lastGallery = gallery;

    var visibleSlides = [];
    slides.forEach(function (slide) {
      var img = slide.querySelector('img[alt]');
      var match = parseGroup(img ? img.alt : '') === group;
      if (match) {
        if (slide.hasAttribute('hidden')) slide.removeAttribute('hidden');
        // Undo the forced inline override from a previous run, if any, so
        // the theme's own CSS/JS (aria-hidden, reveal, etc.) controls it again.
        if (slide.style.display === 'none') slide.style.removeProperty('display');
        visibleSlides.push(slide);
      } else {
        if (!slide.hasAttribute('hidden')) slide.setAttribute('hidden', '');
        // Belt-and-braces: the theme's `[hidden]:not([reveal]) { display: none }`
        // rule loses a cascade-layer fight against a later
        // `.product-media-container { display: flex }` rule, so [hidden] alone
        // leaves the slide laid out at full width — still reachable by a raw
        // drag/swipe gesture even though JS navigation correctly ignores it.
        // An inline style always wins over stylesheet rules, so force it.
        if (slide.style.display !== 'none') slide.style.display = 'none';
      }
    });

    var thumbs = Array.prototype.filter.call(gallery.querySelectorAll('.slideshow-controls__thumbnail'), function (
      el
    ) {
      return !isInDialog(el);
    });

    thumbs.forEach(function (btn) {
      var img = btn.querySelector('img[alt]');
      var altText = img ? img.alt : '';
      var match = parseGroup(altText) === group;

      if (!match) {
        if (!btn.hasAttribute('hidden')) btn.setAttribute('hidden', '');
        return;
      }
      if (btn.hasAttribute('hidden')) btn.removeAttribute('hidden');

      bindThumbnailClick(btn, gallery);
    });

    var slideshowEl = gallery.querySelector('slideshow-component');
    if (slideshowEl && slideshowEl.refs) {
      bindArrowClick(slideshowEl.refs.next, gallery, 'next');
      bindArrowClick(slideshowEl.refs.previous, gallery, 'previous');
    }

    // Only snap back to the group's first slide when the Carnation
    // selection itself changed — not on every incidental mutation (that
    // was what broke swiping: any internal DOM change from a drag/settle
    // would otherwise reset the slide mid-gesture).
    var slideshow = gallery.slideshow;
    if ((groupChanged || galleryReplaced) && slideshow && typeof slideshow.select === 'function' && visibleSlides.length) {
      var scroller = slideshow.querySelector('slideshow-slides');
      if (scroller) scroller.scrollLeft = 0;
      slideshow.select(0, undefined, { animate: false });
    }

    root.classList.remove('carnation-transitioning');
  }

  // Re-asserts aria-selected="true" on the one visible thumbnail matching the
  // slideshow's CURRENT slide (by alt text) and "false" on every other
  // visible thumbnail. Safe to call as often as needed — it's a no-op once
  // state is already correct (guards every write), which is what lets it be
  // used both from the 'slideshow:select' event AND from a MutationObserver
  // on the same attribute without ever looping.
  function correctActiveThumbnails(root) {
    var gallery = root.querySelector('media-gallery');
    if (!gallery) return;

    var slideshow = gallery.slideshow;
    var slides = slideshow && slideshow.slides;
    if (!slides || !slides.length) return;

    var currentSlide = slides[slideshow.current];
    if (!currentSlide) return;

    var simg = currentSlide.querySelector('img[alt]');
    var altText = simg ? simg.alt : null;
    if (!altText) return;

    var thumbs = Array.prototype.filter.call(gallery.querySelectorAll('.slideshow-controls__thumbnail'), function (
      el
    ) {
      return !isInDialog(el);
    });

    thumbs.forEach(function (btn) {
      if (btn.hasAttribute('hidden')) return;
      var img = btn.querySelector('img[alt]');
      var isMatch = !!img && img.alt === altText;
      var newVal = isMatch ? 'true' : 'false';
      if (btn.getAttribute('aria-selected') !== newVal) {
        btn.setAttribute('aria-selected', newVal);
      }
    });
  }

  function setupSection(root) {
    if (root.__carnationFilterSetup) return;
    root.__carnationFilterSetup = true;

    var gallery = root.querySelector('media-gallery');
    if (!gallery) return;

    var state = { lastGroup: null };
    var mutating = false;

    function run() {
      if (mutating) return;
      mutating = true;
      try {
        applyFilter(root, state);
      } finally {
        Promise.resolve().then(function () {
          mutating = false;
        });
      }
    }

    run();

    root.addEventListener('change', function (event) {
      var input = event.target.closest && event.target.closest('input[data-option-name="' + OPTION_NAME + '"]');
      if (!input) return;
      // Hide the gallery instantly so the user doesn't see unfiltered
      // slides while section rendering fetches + replaces the gallery.
      // The class is removed inside applyFilter once filtering is done.
      root.classList.add('carnation-transitioning');
    });

    root.addEventListener('slideshow:select', function () {
      correctActiveThumbnails(root);
    });

    // Belt-and-braces for the 'slideshow:select' listener above: Horizon can
    // rewrite a thumbnail's aria-selected a second time afterwards (via its
    // own scroll-settle sync) without emitting another event, so watch the
    // attribute itself and self-correct whenever it changes — regardless of
    // what triggered it or when. `fixingThumbs` prevents our own corrective
    // writes from re-triggering this same observer.
    var fixingThumbs = false;
    var thumbObserver = new MutationObserver(function () {
      if (fixingThumbs) return;
      fixingThumbs = true;
      correctActiveThumbnails(root);
      Promise.resolve().then(function () {
        fixingThumbs = false;
      });
    });
    thumbObserver.observe(root, { attributes: true, attributeFilter: ['aria-selected'], subtree: true });

    // Scoped to the whole section root, not just the gallery element —
    // Shopify/Horizon replaces the entire <media-gallery> custom element
    // (not just its children) when the variant selection changes, so an
    // observer attached to that specific element goes stale/detached the
    // first time that happens and silently stops re-filtering anything.
    // applyFilter() re-queries `root.querySelector('media-gallery')`
    // fresh on every call, so it always finds whichever instance is
    // currently in the DOM. The unconditional slide-0 reset this used to
    // cause on every unrelated mutation (price, quantity, â€¦) is what's
    // fixed above via `groupChanged` instead of narrowing this scope.
    var observer = new MutationObserver(function () {
      run();
    });
    observer.observe(root, { childList: true, subtree: true });
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      var section = gallery.closest('.shopify-section');
      if (section) setupSection(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
